import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { CreditCard, HandCoins, UserCircle2 } from "lucide-react";
import { cn } from "../../../utils/function";
import { findProfile, PROFILES, SOURCES } from "./data";
import { card, linkCls, PageHeader, Pill, secondaryBtn, SourceIcon, Tabs } from "./ui";

// Unified profile detail (/unify/unified-profiles/:id) — sample profiles
// from data.ts until the profiles API exists.

const Gauge: React.FC<{ value: number; max: number; color: string; label: React.ReactNode }> = ({ value, max, color, label }) => {
  const r = 52;
  const c = 2 * Math.PI * r;
  const len = Math.min(1, value / max) * c;
  return (
    <div className="relative w-32 h-32 shrink-0">
      <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
        <circle cx="60" cy="60" r={r} fill="none" stroke="#EEF0FB" strokeWidth="10" />
        <circle cx="60" cy="60" r={r} fill="none" stroke={color} strokeWidth="10" strokeLinecap="round" strokeDasharray={`${len} ${c}`} />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-3xl font-semibold text-gray-900 dark:text-gray-100">{label}</span>
    </div>
  );
};

const chip = "text-xs bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300 rounded px-1.5 py-0.5";

const UnifiedProfilePage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const p = findProfile(id) || PROFILES[0];
  const [tab, setTab] = useState("Basic Info");

  const basic: [string, React.ReactNode][] = [
    ["Customer ID/ CIF Number", p.cif],
    ["Full Name", p.name],
    ["KYC Status", <Pill tone="green">{p.kyc}</Pill>],
    ["WhatsApp Number", p.whatsapp],
    ["Email", p.email],
    ["Gender", p.gender],
    ["Age group", p.ageGroup],
    ["Interests", <span className="flex flex-wrap justify-end gap-1">{p.interests.map((i) => <span key={i} className={chip}>{i}</span>)}</span>],
    ["Location:", p.location],
    ["Income band", p.incomeBand],
    ["Credit Score", <span className={chip}>810</span>],
  ];

  const traitGroup = (title: string, rows: [string, React.ReactNode][]) => (
    <div key={title}>
      <p className="font-semibold text-gray-900 dark:text-gray-100 py-1.5 border-b border-gray-200 dark:border-gray-800">{title}</p>
      {rows.map(([k, v]) => (
        <div key={k} className="flex items-center justify-between gap-4 py-2">
          <span className="text-gray-500 dark:text-gray-400">{k}</span>
          <span className="flex flex-wrap justify-end gap-1">{v}</span>
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 p-4 sm:p-6">
      <PageHeader crumbs={["Unify", "All Unified Profiles", p.name]} back="/unify/dashboard" />
      <div className="bg-gray-100/80 dark:bg-gray-900/40 border border-t-0 border-gray-200 dark:border-gray-800 rounded-b-xl px-4 sm:px-6 py-6 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <div className={cn(card, "p-5 flex gap-4")}>
            <UserCircle2 size={64} className="text-gray-500 shrink-0" />
            <div className="min-w-0 space-y-2 text-sm">
              <p className="flex flex-wrap items-center gap-2 text-lg text-gray-900 dark:text-gray-100">
                {p.name} <Pill tone="green">{p.kyc}</Pill>
              </p>
              {[
                ["Source Country:", p.country],
                ["CIF Number:", p.cif],
                ["WhatsApp N.:", p.whatsapp],
                ["Email:", p.email],
              ].map(([k, v]) => (
                <p key={k} className="text-gray-500 dark:text-gray-400 break-all">
                  {k} <span className="text-gray-900 dark:text-gray-100">{v}</span>
                </p>
              ))}
            </div>
          </div>
          <div className={cn(card, "p-5 flex flex-col")}>
            <p className="text-lg text-gray-900 dark:text-gray-100">Customer Account Type</p>
            <span className={cn(chip, "mt-3 w-fit text-sm")}>{p.accountType}</span>
            <button type="button" onClick={() => setTab("Traits")} className={cn(linkCls, "mt-auto pt-4 text-left")}>
              View Missing Data
            </button>
          </div>
          <div className={cn(card, "p-5")}>
            <p className="text-lg text-gray-900 dark:text-gray-100">Credit Score</p>
            <div className="mt-3 flex items-center gap-4">
              <Gauge value={p.creditScore} max={900} color="#FCD34D" label={p.creditScore} />
              <div>
                <p className="font-medium text-yellow-700">Good</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Connect recommended sources to enrich this profile</p>
              </div>
            </div>
          </div>
          <div className={cn(card, "p-5")}>
            <p className="text-lg text-gray-900 dark:text-gray-100">Profile Enrichment Coverage</p>
            <div className="mt-3 flex items-center gap-4">
              <Gauge value={p.coverage} max={100} color="#86EFAC" label={`${p.coverage}%`} />
              <div>
                <p className="font-medium text-emerald-600">Good</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">Connect recommended sources to enrich this profile</p>
                <button type="button" onClick={() => navigate("/unify/setup")} className={cn(linkCls, "mt-1")}>
                  Add more connectors
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className={cn(card, "p-5")}>
            <p className="text-lg text-gray-900 dark:text-gray-100 mb-3">Key Information</p>
            <Tabs tabs={["Basic Info", "Traits", "Activity", "External IDs", "Merge History"]} active={tab} onChange={setTab} />
            <div className="mt-3 text-sm">
              {tab === "Basic Info" && (
                <dl className="space-y-2.5">
                  {basic.map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between gap-4">
                      <dt className="text-gray-500 dark:text-gray-400">{k}</dt>
                      <dd className="text-gray-900 dark:text-gray-100 text-right break-all">{v}</dd>
                    </div>
                  ))}
                </dl>
              )}
              {tab === "Traits" && (
                <div className="space-y-2">
                  {traitGroup("Declared Traits", p.traits.declared.map(([k, v]) => [k, v.map((x) => <span key={x} className={chip}>{x}</span>)]))}
                  {traitGroup("Behavioural Traits", p.traits.behavioural.map(([k, v]) => [k, v.map((x) => <span key={x} className={chip}>{x}</span>)]))}
                  {traitGroup("Predictive Traits", p.traits.predictive.map(([k, v]) => [k, <span className="text-gray-900 dark:text-gray-100">{v}</span>]))}
                </div>
              )}
              {tab === "Activity" && (
                <ul className="space-y-3 border-l-2 border-gray-200 dark:border-gray-700 ml-1.5 pl-4">
                  {p.activity.map((a) => (
                    <li key={a} className="relative text-gray-900 dark:text-gray-100">
                      <span className="absolute -left-[22px] top-1.5 w-2.5 h-2.5 rounded-full bg-emerald-500" />
                      {a}
                    </li>
                  ))}
                </ul>
              )}
              {tab === "External IDs" && (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[480px]">
                    <thead>
                      <tr className="text-left font-semibold text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-800">
                        <th className="py-2 pr-3">ID Type</th>
                        <th className="py-2 pr-3">Value</th>
                        <th className="py-2 pr-3">Source</th>
                        <th className="py-2 text-right">Added On</th>
                      </tr>
                    </thead>
                    <tbody>
                      {p.externalIds.map((x) => (
                        <tr key={x.type}>
                          <td className="py-2 pr-3 text-gray-500 dark:text-gray-400">{x.type}</td>
                          <td className="py-2 pr-3 text-gray-900 dark:text-gray-100">{x.value}</td>
                          <td className="py-2 pr-3"><span className={chip}>{x.source}</span></td>
                          <td className="py-2 text-right text-gray-900 dark:text-gray-100 whitespace-nowrap">{x.added}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {tab === "Merge History" && (
                <ul className="divide-y divide-gray-100 dark:divide-gray-800">
                  {p.merges.map((m, i) => (
                    <li key={i} className="py-2.5 flex flex-wrap items-center justify-between gap-2">
                      <span>
                        <span className="block text-gray-900 dark:text-gray-100">{m.source}</span>
                        <span className="text-xs text-gray-500">{m.at} · matched on {m.rule}</span>
                      </span>
                      <Pill>{m.action}</Pill>
                    </li>
                  ))}
                  <li className="pt-3">
                    <button type="button" onClick={() => navigate("/unify/merge-logs")} className={linkCls}>View all merge logs</button>
                  </li>
                </ul>
              )}
            </div>
          </div>

          <div className={cn(card, "p-5 flex flex-col")}>
            <p className="text-lg text-gray-900 dark:text-gray-100">Improve this Profile</p>
            <ul className="mt-3 space-y-4">
              {[
                { icon: CreditCard, title: "Credit and Risk", text: "Missing credit score and credit history", cls: "text-rose-500" },
                { icon: HandCoins, title: "Loan Behaviour", text: "Missing repayment history and loan details if any", cls: "text-indigo-600" },
              ].map(({ icon: Icon, title, text, cls }) => (
                <li key={title} className="flex gap-3">
                  <Icon size={20} className={cn("shrink-0 mt-0.5", cls)} />
                  <div>
                    <p className="text-[15px] text-gray-900 dark:text-gray-100">{title}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{text}</p>
                  </div>
                </li>
              ))}
            </ul>
            <button type="button" onClick={() => navigate("/unify/setup")} className={cn(secondaryBtn, "mt-auto w-fit")}>
              Explore Connectors
            </button>
          </div>
        </div>

        <div>
          <h2 className="text-lg text-gray-900 dark:text-gray-100 mb-3">Connections</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {SOURCES.filter((s) => s.id !== "convohub").map((s) => (
              <div key={s.id} className={cn(card, "p-5 flex gap-3")}>
                <SourceIcon id={s.id} size={22} />
                <div className="min-w-0">
                  <p className="text-[15px] text-gray-900 dark:text-gray-100">{s.name}</p>
                  <Pill tone="green" dot className="mt-1">Connected</Pill>
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{s.description}</p>
                  <span className={cn(chip, "inline-block mt-2")}>4 fields</span>
                  <p className="mt-2 text-sm text-gray-500">Last Sync {s.lastSync}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h2 className="text-lg text-gray-900 dark:text-gray-100 mb-3">Additional Info</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            <div className={cn(card, "p-5")}>
              <p className="text-[15px] text-gray-900 dark:text-gray-100">Recent Activity</p>
              <ul className="mt-3 space-y-2 border-l-2 border-gray-200 dark:border-gray-700 ml-1 pl-4 text-sm">
                {p.activity.map((a) => (
                  <li key={a} className="relative text-gray-900 dark:text-gray-100">
                    <span className="absolute -left-[21px] top-1.5 w-2 h-2 rounded-full bg-emerald-500" />
                    {a}
                  </li>
                ))}
              </ul>
            </div>
            <div className={cn(card, "p-5")}>
              <p className="text-[15px] text-gray-900 dark:text-gray-100">Segments</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {p.segments.map((s) => <span key={s} className={cn(chip, "text-sm")}>{s}</span>)}
              </div>
            </div>
            <div className={cn(card, "p-5")}>
              <p className="text-[15px] text-gray-900 dark:text-gray-100">Recommended Connectors</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Add additional sources to improve profile health</p>
              <ul className="mt-3 space-y-3">
                {["Credit Bureau Data", "Facebook"].map((c) => (
                  <li key={c} className="flex items-center justify-between gap-3">
                    <span className="text-[15px] font-medium text-gray-900 dark:text-gray-100">{c}</span>
                    <button type="button" onClick={() => toast(`${c} connector is coming soon`)} className={secondaryBtn}>Connect</button>
                  </li>
                ))}
              </ul>
              <button type="button" onClick={() => navigate("/unify/setup")} className={cn(linkCls, "mt-3")}>Explore Connectors</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnifiedProfilePage;
